<template>
    <div class="elp">
        <!-- Search + geolocation -->
        <div class="elp-search-row">
            <div class="elp-search-wrap">
                <i class="fal fa-search elp-search-icon"></i>
                <input
                    v-model="searchQuery"
                    type="text"
                    class="elp-search-input"
                    :placeholder="$t('events.form.location_search_placeholder')"
                    @input="onSearchInput"
                    @keydown.enter.prevent="searchAddress"
                />
                <i v-if="searching" class="fal fa-spinner-third fa-spin elp-search-loader"></i>
                <button
                    v-else-if="searchQuery"
                    type="button"
                    class="elp-search-clear"
                    @click="clearSearch"
                >
                    <i class="fal fa-times"></i>
                </button>
            </div>
            <button
                type="button"
                class="elp-geolocate"
                :title="$t('events.form.location_geolocate')"
                :disabled="locating"
                @click="geolocate"
            >
                <i :class="locating ? 'fal fa-spinner-third fa-spin' : 'fal fa-location-arrow'"></i>
            </button>
        </div>

        <!-- Autocomplete results -->
        <div v-if="searchResults.length" class="elp-results">
            <div
                v-for="(result, i) in searchResults"
                :key="i"
                class="elp-result-item"
                @mousedown.prevent="selectResult(result)"
            >
                <i class="fal fa-map-marker-alt"></i>
                <span>{{ result.display_name }}</span>
            </div>
        </div>

        <!-- Map -->
        <div ref="mapContainer" class="elp-map"></div>

        <!-- Footer: address display or hint -->
        <div v-if="geocoding" class="elp-footer elp-footer--loading">
            <i class="fal fa-spinner-third fa-spin"></i>
            <span>{{ $t('events.form.location_geocoding') }}</span>
        </div>
        <div v-else-if="selectedAddress" class="elp-footer elp-footer--address">
            <i class="fal fa-map-marker-alt"></i>
            <span>{{ selectedAddress }}</span>
            <button type="button" class="elp-clear-btn" @click="clearLocation">
                <i class="fal fa-times"></i>
            </button>
        </div>
        <div v-else class="elp-footer elp-footer--hint">
            <i class="fal fa-info-circle"></i>
            <span>{{ $t('events.form.location_hint') }}</span>
        </div>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import L from 'leaflet'

const DEFAULT_CENTER = [46.6031, 1.8883]
const DEFAULT_ZOOM = 6

function buildIcon(color) {
    return L.divIcon({
        className: '',
        html: `<svg viewBox="0 0 28 38" xmlns="http://www.w3.org/2000/svg" style="display:block;width:28px;height:38px;filter:drop-shadow(0 2px 4px rgba(0,0,0,.35))">
            <path d="M14 0C6.268 0 0 6.268 0 14c0 9.6 14 24 14 24S28 23.6 28 14C28 6.268 21.732 0 14 0z" fill="${color}"/>
            <circle cx="14" cy="14" r="6" fill="#fff"/>
        </svg>`,
        iconSize: [28, 38],
        iconAnchor: [14, 38],
        popupAnchor: [0, -40]
    })
}

export default {
    name: 'EventLocationPicker',
    props: {
        value: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            map: null,
            marker: null,
            searchQuery: '',
            searchResults: [],
            searchTimeout: null,
            searching: false,
            locating: false,
            geocoding: false,
            selectedAddress: ''
        }
    },
    mounted() {
        this.$nextTick(() => this.initMap())
    },
    beforeDestroy() {
        clearTimeout(this.searchTimeout)
        if (this.map) this.map.remove()
    },
    methods: {
        initMap() {
            if (!this.$refs.mapContainer) return

            const hasValue = this.value && this.value.lat != null && this.value.lng != null
            const center = hasValue ? [this.value.lat, this.value.lng] : DEFAULT_CENTER
            const zoom = hasValue ? 15 : DEFAULT_ZOOM

            this.map = L.map(this.$refs.mapContainer).setView(center, zoom)

            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
                attribution: '&copy; OpenStreetMap contributors'
            }).addTo(this.map)

            this.map.on('click', this.onMapClick)

            if (hasValue) {
                this.placeMarker(L.latLng(this.value.lat, this.value.lng))
                this.selectedAddress = this.value.address || ''
                this.searchQuery = this.value.address || ''
            }

            setTimeout(() => this.map && this.map.invalidateSize(), 200)
        },

        placeMarker(latlng) {
            if (this.marker) {
                this.marker.setLatLng(latlng)
            } else {
                this.marker = L.marker(latlng, { icon: buildIcon('#0891B2') }).addTo(this.map)
            }
        },

        async onMapClick(e) {
            this.placeMarker(e.latlng)
            this.searchResults = []
            await this.reverseGeocode(e.latlng)
        },

        async reverseGeocode(latlng) {
            this.geocoding = true
            try {
                const res = await fetch(
                    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latlng.lat}&lon=${latlng.lng}&addressdetails=1&accept-language=fr`
                )
                const data = await res.json()
                const address = data.display_name || `${latlng.lat.toFixed(5)}, ${latlng.lng.toFixed(5)}`
                this.selectedAddress = address
                this.searchQuery = address
                this.$emit('input', { address, lat: latlng.lat, lng: latlng.lng })
            } catch {
                const address = `${latlng.lat.toFixed(5)}, ${latlng.lng.toFixed(5)}`
                this.selectedAddress = address
                this.searchQuery = address
                this.$emit('input', { address, lat: latlng.lat, lng: latlng.lng })
            } finally {
                this.geocoding = false
            }
        },

        onSearchInput() {
            clearTimeout(this.searchTimeout)
            this.searchResults = []
            if (this.searchQuery.length < 3) return
            this.searchTimeout = setTimeout(() => this.searchAddress(), 400)
        },

        async searchAddress() {
            if (this.searchQuery.length < 3) return
            this.searching = true
            try {
                const params = new URLSearchParams({
                    q: this.searchQuery,
                    format: 'json',
                    limit: '5',
                    addressdetails: '1',
                    'accept-language': 'fr'
                })
                const res = await fetch(`https://nominatim.openstreetmap.org/search?${params}`)
                const data = await res.json()
                this.searchResults = data.map((r) => ({
                    display_name: r.display_name,
                    lat: r.lat,
                    lon: r.lon
                }))
            } catch {
                this.searchResults = []
            } finally {
                this.searching = false
            }
        },

        selectResult(result) {
            const latlng = L.latLng(parseFloat(result.lat), parseFloat(result.lon))
            this.searchQuery = result.display_name
            this.searchResults = []
            this.selectedAddress = result.display_name
            this.placeMarker(latlng)
            this.map.setView(latlng, 15)
            this.$emit('input', {
                address: result.display_name,
                lat: latlng.lat,
                lng: latlng.lng
            })
        },

        clearSearch() {
            this.searchQuery = ''
            this.searchResults = []
        },

        clearLocation() {
            this.searchQuery = ''
            this.searchResults = []
            this.selectedAddress = ''
            if (this.marker) {
                this.marker.remove()
                this.marker = null
            }
            this.$emit('input', null)
        },

        geolocate() {
            if (!navigator.geolocation) return
            this.locating = true
            navigator.geolocation.getCurrentPosition(
                async (pos) => {
                    const latlng = L.latLng(pos.coords.latitude, pos.coords.longitude)
                    this.placeMarker(latlng)
                    this.map.setView(latlng, 15)
                    this.locating = false
                    await this.reverseGeocode(latlng)
                },
                () => { this.locating = false },
                { enableHighAccuracy: true, timeout: 10000 }
            )
        }
    }
}
</script>

<style scoped>
.elp {
    display: flex;
    flex-direction: column;
    gap: 0;
}

.elp-search-row {
    display: flex;
    gap: 8px;
    margin-bottom: 4px;
}

.elp-search-wrap {
    flex: 1;
    position: relative;
    display: flex;
    align-items: center;
}

.elp-search-icon {
    position: absolute;
    left: 10px;
    color: var(--v2-text-3);
    font-size: 0.85rem;
    pointer-events: none;
}

.elp-search-input {
    width: 100%;
    padding: 8px 34px 8px 32px;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-surface-1);
    color: var(--v2-text-1);
    font-size: 0.875rem;
    font-family: var(--v2-font-sans);
    outline: none;
    transition: border-color 0.15s;
}

.elp-search-input:focus {
    border-color: var(--v2-primary);
}

.elp-search-loader,
.elp-search-clear {
    position: absolute;
    right: 10px;
    color: var(--v2-text-3);
    font-size: 0.85rem;
}

.elp-search-clear {
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px 4px;
    display: flex;
    align-items: center;
}

.elp-search-clear:hover {
    color: var(--v2-text-1);
}

.elp-geolocate {
    padding: 8px 12px;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-surface-1);
    color: var(--v2-text-2);
    cursor: pointer;
    font-size: 0.875rem;
    transition: background 0.15s, border-color 0.15s;
    flex-shrink: 0;
}

.elp-geolocate:hover:not(:disabled) {
    background: var(--v2-surface-2);
    border-color: var(--v2-primary);
    color: var(--v2-primary);
}

.elp-geolocate:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.elp-results {
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-surface-0);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    margin-bottom: 8px;
    z-index: 10;
}

.elp-result-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 9px 12px;
    font-size: 0.85rem;
    color: var(--v2-text-2);
    cursor: pointer;
    transition: background 0.12s;
    border-bottom: 1px solid var(--v2-border);
}

.elp-result-item:last-child {
    border-bottom: none;
}

.elp-result-item:hover {
    background: var(--v2-surface-1);
    color: var(--v2-text-1);
}

.elp-result-item i {
    color: var(--v2-primary);
    margin-top: 2px;
    flex-shrink: 0;
}

.elp-map {
    height: 300px;
    border-radius: var(--v2-radius-md);
    overflow: hidden;
    border: 1px solid var(--v2-border);
    margin-top: 4px;
}

.elp-footer {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: var(--v2-radius-md);
    font-size: 0.82rem;
    margin-top: 8px;
}

.elp-footer--hint {
    background: var(--v2-surface-1);
    color: var(--v2-text-3);
    border: 1px dashed var(--v2-border);
}

.elp-footer--address {
    background: #ecfdf5;
    color: #065f46;
    border: 1px solid #a7f3d0;
}

.elp-footer--loading {
    background: var(--v2-surface-1);
    color: var(--v2-text-3);
    border: 1px solid var(--v2-border);
}

.elp-footer--address i:first-child {
    color: #059669;
    flex-shrink: 0;
}

.elp-footer--address span {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.elp-clear-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: #059669;
    padding: 2px 4px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    transition: background 0.12s;
}

.elp-clear-btn:hover {
    background: rgba(5, 150, 105, 0.12);
}
</style>
